import { stringify as stringifyQs } from 'qs'
import urlJoin from 'url-join'

export const reportSection = '/reports/'

export const dateUrlFormat = 'yyyy-LL-dd'

export const reportAssignedLeadsPath = 'assigned-leads/'
export enum ReportAssignedLeadsType {
  daily = 'daily',
  hourly = 'hourly',
}

export const reportAssignedLeadsByTypePath = (type?: ReportAssignedLeadsType) =>
  type
    ? urlJoin(reportSection, reportAssignedLeadsPath, type)
    : urlJoin(reportSection, reportAssignedLeadsPath)

export interface ReportAssignedLeadsUrlQueryParams {
  since?: string
  until?: string
}

export const reportAssignedLeadsUrl = (
  type?: ReportAssignedLeadsType,
  params?: ReportAssignedLeadsUrlQueryParams,
) => reportAssignedLeadsByTypePath(type) + '?' + stringifyQs(params)

export const reportNewLeadsPath = 'new-leads/'
export enum ReportNewLeadsType {
  daily = 'daily',
  hourly = 'hourly',
}

export const reportNewLeadsByTypePath = (type?: ReportNewLeadsType) =>
  type
    ? urlJoin(reportSection, reportNewLeadsPath, type)
    : urlJoin(reportSection, reportNewLeadsPath)

export interface ReportNewLeadsUrlQueryParams {
  since?: string
  until?: string
}

export const reportNewLeadsUrl = (
  type?: ReportNewLeadsType,
  params?: ReportNewLeadsUrlQueryParams,
) => reportNewLeadsByTypePath(type) + '?' + stringifyQs(params)

// Activity Report Leads

export const reportUserActivityByTypePath = (type?: ReportUserActivityType) =>
  type
    ? urlJoin(reportSection, reportUserActivityPath, type)
    : urlJoin(reportSection, reportUserActivityPath)

export interface ReportUserActivityQueryParams {
  since?: string
  until?: string
}

export const reportUserActivityPath = 'user-activity/'
export enum ReportUserActivityType {
  idleTime = 'idle-time',
}

export const reportUserActivityUrl = (
  type?: ReportUserActivityType,
  params?: ReportUserActivityQueryParams,
) => reportUserActivityByTypePath(type) + '?' + stringifyQs(params)

// Fees (finance)

export const reportFeesPath = 'fees/'
export enum ReportFeesType {
  /** Cohort collection curve + receivables ageing, both keyed on the day the
   * fee was billed. A different basis from the other two tabs, which count
   * money in the window it moved in. */
  collection = 'collection',
  evolution = 'evolution',
  month = 'month',
}

export const reportFeesByTypePath = (type?: ReportFeesType) =>
  type ? urlJoin(reportSection, reportFeesPath, type) : urlJoin(reportSection, reportFeesPath)

export interface ReportFeesUrlQueryParams {
  currency?: string
  dimension?: string
  group?: string
  month?: string
  period?: string
  since?: string
  until?: string
}

export const reportFeesUrl = (type?: ReportFeesType, params?: ReportFeesUrlQueryParams) =>
  reportFeesByTypePath(type) + '?' + stringifyQs(params)

// Acquisition funnel

export const reportAcquisitionFunnelPath = 'acquisition-funnel/'
/**
 * The report's tabs, in reading order.
 *
 * SPLIT ALONG WHAT `Group by` MEANS. `endToEnd` is window-wide and source-
 * agnostic — the Sankey and the website funnel answer for every source there
 * is, and neither can be narrowed to one group — while every block on the
 * other three tabs obeys the focus. Holding both kinds under one Group by
 * control is what made the control look broken: half the page moved and half
 * did not, and the only thing saying so was a caption above a large chart.
 */
export enum ReportAcquisitionFunnelType {
  endToEnd = 'end-to-end',
  dropOff = 'drop-off',
  why = 'why',
  financial = 'financial',
  /** How much of each lead's origin was recorded, and by which producer —
   * the explanation of the other tabs' Unattributed remainder. Window-wide
   * like `endToEnd`: it audits the source columns, so it cannot be narrowed
   * by them. */
  attribution = 'attribution',
}

export const reportAcquisitionFunnelByTypePath = (type?: ReportAcquisitionFunnelType) =>
  type
    ? urlJoin(reportSection, reportAcquisitionFunnelPath, type)
    : urlJoin(reportSection, reportAcquisitionFunnelPath)

export interface ReportAcquisitionFunnelUrlQueryParams {
  /** `'1'` narrows every cohort figure to attributed leads — cases with a
   * source kind, i.e. the ones that came through ads or the website. */
  attributed?: string
  currency?: string
  dimension?: string
  group?: string
  period?: string
  since?: string
  step?: string
  until?: string
}

export const reportAcquisitionFunnelUrl = (
  type?: ReportAcquisitionFunnelType,
  params?: ReportAcquisitionFunnelUrlQueryParams,
) => reportAcquisitionFunnelByTypePath(type) + '?' + stringifyQs(params)

// Lead duplicates

export const reportLeadDuplicatesPath = 'lead-duplicates/'

export interface ReportLeadDuplicatesUrlQueryParams {
  dimension?: string
  group?: string
  period?: string
  since?: string
  until?: string
}

export const reportLeadDuplicatesUrl = (params?: ReportLeadDuplicatesUrlQueryParams) =>
  urlJoin(reportSection, reportLeadDuplicatesPath) + '?' + stringifyQs(params)

// Messaging (WhatsApp delivery, reply rates, conversation activity)

export const reportMessagingPath = 'messaging/'
export enum ReportMessagingType {
  /** Conversation starts, volume by source, handoffs, first-response times. */
  activity = 'activity',
  /** Outbound delivery funnel, failures by Meta code, latency, billing. */
  delivery = 'delivery',
  /** Customer reply rates over the template sends. */
  replies = 'replies',
}

export const reportMessagingByTypePath = (type?: ReportMessagingType) =>
  type
    ? urlJoin(reportSection, reportMessagingPath, type)
    : urlJoin(reportSection, reportMessagingPath)

export interface ReportMessagingUrlQueryParams {
  dimension?: string
  group?: string
  period?: string
  since?: string
  until?: string
}

export const reportMessagingUrl = (
  type?: ReportMessagingType,
  params?: ReportMessagingUrlQueryParams,
) => reportMessagingByTypePath(type) + '?' + stringifyQs(params)
